/* eslint-disable no-undef */
// NOTE: Adapted from: https://github.com/umdjs/umd/blob/master/templates/returnExports.js
(function (root, factory) {
    'use strict';
    if (typeof define === 'function' && define.amd) {
        // AMD
        define([], function () {
            return factory();
        });
    } else if (typeof module === 'object' && module.exports) {
        // CommonJS
        module.exports = factory();
    } else {
        // Browser globals
        root.GoogleAdWordsPixel = factory();
    }
    // eslint-disable-next-line no-invalid-this
})(this, function () {
    'use strict';

    /**
     * This class allows developers to track conversion events to one or more Google Ads (AdWords) pixels
     * using the modern gtag.js API instead of the legacy google_trackConversion API.
     *
     * The main methods within this class are:
     *
     * init
     *      Init's arguments are:
     *          `trackingDataArray` - array of objects
     *             - Each object contains a `conversionId` (e.g., 'AW-123456789') and optional `events` configuration
     *             - The `conversionId` is your Google Ads Conversion ID
     *             - The `events` object maps event names to conversion labels
     *             - Optional `remarketing` flag for remarketing-only tracking
     *      The init method initializes all Google Ads conversion tracking pixels on the page using gtag.js
     *      so that you can then track conversion events across multiple pixels.
     *      Reference: https://support.google.com/google-ads/answer/6331314
     *
     * track
     *      Track's arguments are:
     *          `eventName` - string
     *          `data` (optional) - null or object
     *              - The `eventName` is the event that you want to send to Google Ads
     *              - The `data` argument is data that you can pass to the event (e.g., revenue)
     *      The track method allows you to send conversion information to Google Ads about actions the user takes.
     *      Conversions are tracked across all initialized pixels based on the events configuration.
     *      The Pixel needs to be initialized on the page, by calling init(), before you can successfully use this method.
     *
     * completedOrder
     *      CompletedOrder's arguments are:
     *          `track` - object with properties() method that includes revenue and currency
     *      This method handles order completion events specifically, tracking revenue and currency for e-commerce conversions.
     *
     * page
     *      Page's arguments are:
     *          `pageName` (optional) - string
     *      This method fires pageview tracking, primarily used for remarketing purposes when no conversion events are configured.
     */

    const GoogleAdWordsPixel = class {
        constructor(debug = false, trackingData = null) {
            this.initialized = !!(
                typeof window !== 'undefined' && window.userGtag
            );
            this.debug = debug;
            this.trackingData = trackingData;
        }

        _error(message) {
            console.error(`${message}`);
        }

        _log(message) {
            console.log(`${message}`);
        }

        _verifyInit() {
            // Check all required conditions for pixel to be properly initialized
            const hasWindowUserGtag = !!(
                typeof window !== 'undefined' && window.userGtag
            );
            const hasUserLayer = !!(
                typeof window !== 'undefined' && window.userLayer
            );
            const hasTrackingData = !!(
                this.trackingData &&
                Array.isArray(this.trackingData) &&
                this.trackingData.length > 0
            );
            const isInitialized = this.initialized;

            const isFullyInitialized =
                hasWindowUserGtag &&
                hasUserLayer &&
                hasTrackingData &&
                isInitialized;

            if (!isFullyInitialized && this.debug) {
                this._log(
                    `GOOGLE_ADS: Pixel not fully initialized - ` +
                        `window.userGtag: ${hasWindowUserGtag}, ` +
                        `window.userLayer: ${hasUserLayer}, ` +
                        `trackingData: ${hasTrackingData}, ` +
                        `initialized flag: ${isInitialized}`,
                );
            }

            return isFullyInitialized;
        }

        _normalizeConversionId(conversionId) {
            // Normalize conversion ID to ensure it has the AW- prefix
            // Accepts both "AW-123456789" and "123456789" formats
            if (!conversionId) return null;

            const id = String(conversionId).trim();

            // If it already has AW- prefix, return as-is
            if (id.toUpperCase().startsWith('AW-')) {
                return id;
            }

            // If it's just numbers, add the AW- prefix
            if (/^\d+$/.test(id)) {
                return `AW-${id}`;
            }

            // If it has other format, log warning and return as-is
            if (this.debug) {
                this._log(
                    `GOOGLE_ADS: Warning - Unusual conversion ID format: ${id}. Expected "AW-XXXXXXXXX" or "XXXXXXXXX"`,
                );
            }
            return id;
        }

        _gtagCode() {
            // Initialize Google Tag (gtag.js) with custom userLayer and userGtag
            if (typeof window === 'undefined') return;

            // Initialize userLayer if it doesn't exist
            window.userLayer = window.userLayer || [];

            // Define userGtag function if it doesn't exist
            if (!window.userGtag) {
                window.userGtag = function () {
                    window.userLayer.push(arguments);
                };
            }

            // Set the initial timestamp
            window.userGtag('js', new Date());
        }

        _loadGtagScript(conversionId) {
            // Load the gtag.js script dynamically
            if (typeof document === 'undefined') return;

            // Check if script is already loaded for this conversion ID
            const scriptId = `gtag-script-${conversionId}`;
            if (document.getElementById(scriptId)) {
                if (this.debug) {
                    this._log(
                        `GOOGLE_ADS: Script already loaded for ${conversionId}`,
                    );
                }
                return;
            }

            const script = document.createElement('script');
            script.id = scriptId;
            script.async = true;
            // Add l=userLayer to sandbox the data layer
            script.src = `https://www.googletagmanager.com/gtag/js?id=${conversionId}&l=userLayer`;

            const firstScript = document.getElementsByTagName('script')[0];
            if (firstScript && firstScript.parentNode) {
                firstScript.parentNode.insertBefore(script, firstScript);
            } else {
                document.head.appendChild(script);
            }
        }

        init(trackingDataArray) {
            if (
                !trackingDataArray ||
                !Array.isArray(trackingDataArray) ||
                trackingDataArray.length === 0
            ) {
                if (this.debug) {
                    this._error(
                        'GOOGLE_ADS: Need to insert tracking data array for initializing',
                    );
                }
                return;
            }

            // Initialize userGtag code first
            this._gtagCode();
            this.initialized = true;

            if (this.debug) {
                this._log(
                    `GOOGLE_ADS: Tracking Data ${JSON.stringify(
                        trackingDataArray,
                    )}`,
                );
            }

            // Normalize and deduplicate pixels by conversionId to prevent double-firing of events
            const seenConversionIds = new Set();
            const deduplicatedTrackingData = trackingDataArray
                .map((pixelData) => {
                    // Normalize the conversion ID to ensure AW- prefix
                    const originalId = pixelData.conversionId;
                    const normalizedId =
                        this._normalizeConversionId(originalId);

                    if (!normalizedId) return null;

                    if (this.debug && originalId !== normalizedId) {
                        this._log(
                            `GOOGLE_ADS: Normalized conversion ID from "${originalId}" to "${normalizedId}"`,
                        );
                    }

                    // Return new object with normalized conversionId
                    return {
                        ...pixelData,
                        conversionId: normalizedId,
                    };
                })
                .filter((pixelData) => {
                    if (!pixelData) return false;

                    const conversionId = pixelData.conversionId;
                    if (!conversionId) return false;

                    if (seenConversionIds.has(conversionId)) {
                        if (this.debug) {
                            this._log(
                                `GOOGLE_ADS: Skipping duplicate conversion ID: ${conversionId}`,
                            );
                        }
                        return false;
                    }

                    seenConversionIds.add(conversionId);
                    return true;
                });

            // Store deduplicated tracking data for use in track(), completedOrder(), and page() methods
            this.trackingData = deduplicatedTrackingData;

            // Load gtag.js script and configure each conversion ID
            deduplicatedTrackingData.forEach((pixelData) => {
                const conversionId = pixelData.conversionId;
                if (this.debug) {
                    this._log(
                        `GOOGLE_ADS: Initializing conversion ID: ${conversionId}`,
                    );
                }

                // Load the gtag.js script for this conversion ID
                this._loadGtagScript(conversionId);

                // Configure the conversion ID
                if (window.userGtag) {
                    window.userGtag('config', conversionId);
                }
            });
        }

        track(eventName, data = null) {
            if (!this._verifyInit()) return;

            if (!this.trackingData || !Array.isArray(this.trackingData)) return;

            if (!window.userGtag) return;

            // Handle "Completed Order" event by routing to completedOrder method
            // This matches analytics.js behavior where "Completed Order" event
            // is automatically routed to completedOrder()
            if (eventName === 'Completed Order') {
                // Create a track-like object with the data
                const trackObj = {
                    revenue: data && data.revenue,
                    properties: () => data || {},
                };
                this.completedOrder(trackObj);
                return;
            }

            // Skip event_order_confirmation as it's handled by completedOrder()
            if (eventName === 'event_order_confirmation') {
                return;
            }

            // Track conversion events for all initialized pixels
            this.trackingData.forEach((pixelData) => {
                const conversionId = pixelData.conversionId;
                if (!conversionId) return;

                const trackingEvents = pixelData.events;

                // Get the conversion label for this event
                const conversionLabel = trackingEvents
                    ? trackingEvents[eventName]
                    : null;

                if (!conversionLabel) {
                    if (this.debug) {
                        this._log(
                            `GOOGLE_ADS: No conversion label configured for event ${eventName} on conversion ID ${conversionId}`,
                        );
                    }
                    return;
                }

                // Build the conversion payload
                const payload = {
                    send_to: `${conversionId}/${conversionLabel}`,
                };

                if (this.debug) {
                    this._log(
                        `GOOGLE_ADS: Tracking conversion event: ${eventName} with payload: ${JSON.stringify(
                            payload,
                        )}`,
                    );
                }

                // Fire the conversion event
                window.userGtag('event', 'conversion', payload);
            });
        }

        completedOrder(track) {
            if (!this._verifyInit()) return;

            if (!this.trackingData || !Array.isArray(this.trackingData)) return;

            if (!window.userGtag) return;

            // Get revenue and currency from track properties
            const revenue =
                (track && typeof track.revenue === 'function'
                    ? track.revenue()
                    : track && track.revenue) || 0;
            const props =
                track && typeof track.properties === 'function'
                    ? track.properties()
                    : track || {};
            const currency = props.currency || 'USD';

            // Track conversion for order completion across all initialized pixels
            this.trackingData.forEach((pixelData) => {
                const conversionId = pixelData.conversionId;
                if (!conversionId) return;

                const trackingEvents = pixelData.events;

                // Get the conversion label for event_order_confirmation
                const conversionLabel = trackingEvents
                    ? trackingEvents['event_order_confirmation']
                    : null;

                if (!conversionLabel) {
                    if (this.debug) {
                        this._log(
                            `GOOGLE_ADS: No conversion label configured for event_order_confirmation on conversion ID ${conversionId}`,
                        );
                    }
                    return;
                }

                // Build the conversion payload with revenue and currency
                const payload = {
                    send_to: `${conversionId}/${conversionLabel}`,
                    value: revenue,
                    currency: currency,
                };

                // Add transaction ID if available
                if (props.transaction_id || props.orderId) {
                    payload.transaction_id =
                        props.transaction_id || props.orderId;
                }

                if (this.debug) {
                    this._log(
                        `GOOGLE_ADS: Tracking completed order with payload: ${JSON.stringify(
                            payload,
                        )}`,
                    );
                }

                // Fire the conversion event
                window.userGtag('event', 'conversion', payload);
            });
        }

        page(pageName) {
            if (!this._verifyInit()) return;

            if (!this.trackingData || !Array.isArray(this.trackingData)) return;

            if (!window.userGtag) return;

            // Track page view for remarketing purposes
            this.trackingData.forEach((pixelData) => {
                const conversionId = pixelData.conversionId;

                // Always fire page tracking
                if (this.debug) {
                    this._log(
                        `GOOGLE_ADS: Tracking page view on conversion ID ${conversionId}${
                            pageName ? ` (${pageName})` : ''
                        }`,
                    );
                }

                // Fire a pageview event
                // Using config with page_path
                const configData = {};
                if (pageName) {
                    configData.page_path = pageName;
                }
                window.userGtag('config', conversionId, configData);
            });
        }
    };

    return GoogleAdWordsPixel;
});
